/**
 * A cancellation the owner asked for, and what it publishes: nothing, unless
 * the job had already produced something.
 *
 * Every job cancellation goes through the request's cancel marker
 * (requestCancellation.ts): the client posts a stop or a superseding voice
 * turn, the marker is written, and the gptAnswer route, the orchestration
 * runner and process-job mark their job `cancelled` when they read it. So a
 * cancelled job is always one the owner stopped, and a cancellation with no
 * result is not news. A job stopped mid-chain after earlier steps produced
 * components keeps its row: that partial result is still the owner's to open.
 * A stop the owner did not ask for ends `failed` instead (a timed-out job
 * carries `STALE_JOB_ERROR_CODE`), and its receipt stays in the feed.
 *
 * No imports: the feed lifecycle reads this on the client too.
 */

const CANCEL_MARKER_PREFIX = "cancel-marker-";

/** The jobTasks id of a request's cancel marker. The silo looks it up by this id. */
export function getCancellationMarkerId(reactId: string): string {
  return `${CANCEL_MARKER_PREFIX}${reactId}`;
}

/** Whether a job id names a cancel marker: bookkeeping, never a task. */
export function isCancellationMarkerJobId(jobId: unknown): boolean {
  return typeof jobId === "string" && jobId.startsWith(CANCEL_MARKER_PREFIX);
}

/**
 * Whether a job's completion is an owner's cancellation with nothing to show:
 * a cancelled job that produced no components, or a cancel marker, which
 * never produces any. A cancelled outcome without a component count is kept,
 * since nothing says it is empty.
 */
export function isEmptyOwnerCancellation(jobId: unknown, outcome: { status?: unknown; componentCount?: unknown } | null | undefined): boolean {
  if (isCancellationMarkerJobId(jobId)) return true;
  return outcome?.status === "cancelled" && outcome.componentCount === 0;
}

const record = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : null;

const finiteTime = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

/**
 * When a job completion row about an owner's cancellation with nothing to
 * show stops being shown (ms epoch): when it was stored, so it is over at
 * once. Null for every other row, including a cancellation that kept a
 * partial result. Written rows are pulled in on write (the notification
 * normalizer applies this end to `expiresAt`); rows published before are
 * hidden at read. A row without `jobOutcome` (published before rows carried
 * it) is caught only when its job is a cancel marker, which its id tells.
 */
export function ownerCancelledCompletionRelevantUntil(row: Record<string, unknown>): number | null {
  if (row.notificationType !== "task_update" || row.sourceObjectType !== "job") return null;
  const outcome = record(row.jobOutcome) ?? record(record(row.extraFields)?.jobOutcome);
  if (!isEmptyOwnerCancellation(row.sourceObjectId, outcome)) return null;
  // A row with neither time is over since the epoch's first instant; 0 would read as "no end".
  return finiteTime(row.storedDate) ?? finiteTime(row.createdDate) ?? 1;
}
